import React, { useState, useMemo, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Calculator, Zap, Wrench, Router, Printer, Flame, ArrowRight, Construction, X, Send, CheckCircle, ShoppingBag, Shield, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { IMAGES, bgStyle } from '@/config/images';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Password strength calculator
const calculatePasswordStrength = (password) => {
  if (!password) return { score: 0, label: '', color: '' };
  
  let score = 0;
  const checks = {
    length: password.length >= 9,
    lowercase: /[a-z]/.test(password),
    uppercase: /[A-Z]/.test(password),
    numbers: /[0-9]/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  };
  
  if (checks.length) score += 1;
  if (checks.lowercase) score += 1;
  if (checks.uppercase) score += 1;
  if (checks.numbers) score += 1;
  if (checks.special) score += 1;
  
  if (password.length >= 12) score += 1;
  if (password.length >= 16) score += 1;
  
  if (score <= 2) return { score, label: 'Weak', color: 'bg-red-500', textColor: 'text-red-500', checks };
  if (score <= 4) return { score, label: 'Fair', color: 'bg-yellow-500', textColor: 'text-yellow-500', checks };
  if (score <= 5) return { score, label: 'Good', color: 'bg-blue-500', textColor: 'text-blue-500', checks };
  return { score, label: 'Strong', color: 'bg-green-500', textColor: 'text-green-500', checks };
};

// Password Strength Indicator Component
const PasswordStrengthIndicator = ({ password }) => {
  const strength = useMemo(() => calculatePasswordStrength(password), [password]);
  
  if (!password) return null;
  
  return (
    <div className="mt-2 space-y-2">
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 bg-zinc-700 rounded-full overflow-hidden">
          <div 
            className={`h-full transition-all duration-300 ${strength.color}`}
            style={{ width: `${Math.min(100, (strength.score / 7) * 100)}%` }}
          />
        </div>
        <span className={`text-xs font-medium ${strength.textColor}`}>
          {strength.label}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-1 text-xs">
        <div className={strength.checks?.length ? 'text-green-400' : 'text-zinc-500'}>
          {strength.checks?.length ? '✓' : '○'} 9+ characters
        </div>
        <div className={strength.checks?.uppercase ? 'text-green-400' : 'text-zinc-500'}>
          {strength.checks?.uppercase ? '✓' : '○'} Uppercase
        </div>
        <div className={strength.checks?.lowercase ? 'text-green-400' : 'text-zinc-500'}>
          {strength.checks?.lowercase ? '✓' : '○'} Lowercase
        </div>
        <div className={strength.checks?.numbers ? 'text-green-400' : 'text-zinc-500'}>
          {strength.checks?.numbers ? '✓' : '○'} Number
        </div>
        <div className={strength.checks?.special ? 'text-green-400' : 'text-zinc-500'}>
          {strength.checks?.special ? '✓' : '○'} Special char
        </div>
      </div>
    </div>
  );
};

// Country list for dropdown
const COUNTRIES = [
  "Afghanistan", "Albania", "Algeria", "Argentina", "Australia", "Austria", "Bangladesh", 
  "Belgium", "Brazil", "Canada", "Chile", "China", "Colombia", "Czech Republic", "Denmark", 
  "Egypt", "Finland", "France", "Germany", "Greece", "Hong Kong", "Hungary", "India", 
  "Indonesia", "Ireland", "Israel", "Italy", "Japan", "Kenya", "Malaysia", "Mexico", 
  "Netherlands", "New Zealand", "Nigeria", "Norway", "Pakistan", "Peru", "Philippines", 
  "Poland", "Portugal", "Romania", "Russia", "Saudi Arabia", "Singapore", "South Africa", 
  "South Korea", "Spain", "Sweden", "Switzerland", "Taiwan", "Thailand", "Turkey", 
  "Ukraine", "United Arab Emirates", "United Kingdom", "United States", "Vietnam", "Other"
];

const VALID_MODULES = ['cnc_routing', '3d_printing', 'laser_cutting'];

const Landing = () => {
  const [searchParams] = useSearchParams();
  const [showTesterForm, setShowTesterForm] = useState(false);
  const [testerFormDiscipline, setTesterFormDiscipline] = useState('cnc_routing'); // Track which discipline the form is for
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    surname: '',
    country: '',
    email: '',
    password: '',
    confirm_password: ''
  });
  
  // Notify Me modal state
  const [showNotifyForm, setShowNotifyForm] = useState(false);
  const [notifyFeature, setNotifyFeature] = useState(null); // "3d_printing" or "laser_cutting"
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notifyName, setNotifyName] = useState('');
  const [notifySubmitted, setNotifySubmitted] = useState(false);
  const [isNotifySubmitting, setIsNotifySubmitting] = useState(false);

  // Pre-open apply form if ?module= query param is present (e.g. redirected from RequireModule guard)
  useEffect(() => {
    const moduleParam = searchParams.get('module');
    if (moduleParam && VALID_MODULES.includes(moduleParam)) {
      setTesterFormDiscipline(moduleParam);
      setShowTesterForm(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Open tester form for a specific discipline
  const openTesterForm = (discipline) => {
    setTesterFormDiscipline(discipline);
    setShowTesterForm(true);
    setFormSubmitted(false);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all fields
    if (!formData.first_name.trim() || !formData.surname.trim() || !formData.country || !formData.email.trim() || !formData.password) {
      toast.error('Please fill in all fields');
      return;
    }

    // Validate password match
    if (formData.password !== formData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }

    // Validate password strength
    if (formData.password.length < 9) {
      toast.error('Password must be at least 9 characters');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/api/tester-applications`, {
        first_name: formData.first_name,
        surname: formData.surname,
        country: formData.country,
        email: formData.email,
        password: formData.password,
        discipline: testerFormDiscipline // Include discipline in application
      });
      setFormSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit application';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeForm = () => {
    setShowTesterForm(false);
    setFormSubmitted(false);
    setFormData({ first_name: '', surname: '', country: '', email: '', password: '', confirm_password: '' });
  };

  // Notify Me handlers
  const openNotifyForm = (feature) => {
    setNotifyFeature(feature);
    setShowNotifyForm(true);
    setNotifySubmitted(false);
    setNotifyEmail('');
    setNotifyName('');
  };

  const closeNotifyForm = () => {
    setShowNotifyForm(false);
    setNotifyFeature(null);
    setNotifySubmitted(false);
    setNotifyEmail('');
    setNotifyName('');
  };

  const handleNotifySubmit = async (e) => {
    e.preventDefault();
    
    if (!notifyEmail.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    setIsNotifySubmitting(true);
    try {
      await axios.post(`${API}/api/tester-applications/notify-me`, {
        email: notifyEmail,
        feature: notifyFeature,
        name: notifyName || null
      });
      setNotifySubmitted(true);
      toast.success('You\'re on the list!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to subscribe';
      toast.error(message);
    } finally {
      setIsNotifySubmitting(false);
    }
  };

  const getFeatureName = () => {
    return notifyFeature === '3d_printing' ? '3D Printing' : 'Laser Cutting';
  };

  return (
    <div className="min-h-screen bg-[#1a1a1a] text-white">
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center"
        style={{
          backgroundImage: `url('${IMAGES.PLATFORM_HERO_BG}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Dark Gradient Overlay - Strong for text readability across all 3 panels */}
        <div 
          className="absolute inset-0" 
          style={{
            background: 'linear-gradient(to bottom, rgba(10,10,10,0.75), rgba(10,10,10,0.85))'
          }}
        />
        
        {/* Hero Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-4 text-center">
          {/* Hero Logo - Large and Prominent */}
          <div className="mb-10">
            <img 
              src={require('@/assets/logo.png')} 
              alt="CNC-Helpdesk" 
              className="h-[14rem] sm:h-[18rem] md:h-[22rem] lg:h-[26rem] w-auto mx-auto drop-shadow-2xl"
              style={{ background: 'transparent' }}
            />
          </div>

          {/* Discipline Selection Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-4xl mx-auto mb-10">
            {/* Router Edition - TESTERS NEEDED */}
            <div className="group">
              <div className="bg-[#2c2c2c]/90 border-2 border-[#e67e22] rounded-sm p-5 hover:bg-[#3a3a3a]/90 transition-all hover:shadow-[0_0_20px_rgba(230,126,34,0.3)]">
                <div className="flex flex-col items-center text-center">
                  <div className="w-14 h-14 bg-[#1f1f1f] border border-[#e67e22] rounded-full flex items-center justify-center mb-3 group-hover:bg-[#e67e22]/20 transition-colors">
                    <Router className="w-7 h-7 text-[#e67e22]" />
                  </div>
                  <h3 className="text-lg font-bold text-white uppercase tracking-wide mb-1" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    Router Edition
                  </h3>
                  <p className="text-xs text-[#a3a3a3] mb-3">CNC Router & Milling</p>
                  
                  {/* Two buttons - Login and Apply */}
                  <div className="flex flex-col gap-2 w-full">
                    <Link to="/auth" className="w-full">
                      <button className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-[#e67e22] hover:bg-[#d35400] rounded-sm transition-colors">
                        <span className="text-sm font-bold text-white uppercase tracking-wider">
                          Tester Login
                        </span>
                        <ArrowRight className="w-4 h-4 text-white" />
                      </button>
                    </Link>
                    <button 
                      onClick={() => openTesterForm('cnc_routing')}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-[#2c2c2c] hover:bg-[#3a3a3a] border border-[#e67e22] rounded-sm transition-colors"
                    >
                      <span className="text-xs font-medium text-[#e67e22] uppercase tracking-wider">
                        Apply to be a Tester
                      </span>
                    </button>
                  </div>
                  
                  <div className="flex items-center gap-2 mt-4">
                    <span className="w-3 h-3 bg-[#27ae60] rounded-full animate-ping absolute" />
                    <span className="w-3 h-3 bg-[#27ae60] rounded-full relative" />
                    <span className="text-sm font-bold text-[#27ae60] uppercase tracking-wide animate-pulse drop-shadow-[0_0_8px_rgba(39,174,96,0.8)]">
                      Available Now
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* 3D Printing Edition - NOW AVAILABLE */}
            <div className="group">
              <div className="bg-[#2c2c2c]/90 border-2 border-orange-500 rounded-sm p-5 hover:bg-[#3a3a3a]/90 transition-all hover:shadow-[0_0_20px_rgba(249,115,22,0.3)]">
                <div className="flex flex-col items-center text-center">
                  <div className="w-14 h-14 bg-[#1f1f1f] border border-orange-500 rounded-full flex items-center justify-center mb-3 group-hover:bg-orange-500/20 transition-colors">
                    <Printer className="w-7 h-7 text-orange-500" />
                  </div>
                  <h3 className="text-lg font-bold text-white uppercase tracking-wide mb-1" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    3D Printing Edition
                  </h3>
                  <p className="text-xs text-[#a3a3a3] mb-3">FDM & Resin Printers</p>
                  
                  {/* Two buttons - Login and Apply */}
                  <div className="flex flex-col gap-2 w-full">
                    <Link to="/auth?discipline=3d_printing" className="w-full">
                      <button className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 rounded-sm transition-colors">
                        <span className="text-sm font-bold text-white uppercase tracking-wider">
                          Tester Login
                        </span>
                        <ArrowRight className="w-4 h-4 text-white" />
                      </button>
                    </Link>
                    <button 
                      onClick={() => openTesterForm('3d_printing')}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-[#2c2c2c] hover:bg-[#3a3a3a] border border-orange-500 rounded-sm transition-colors"
                    >
                      <span className="text-xs font-medium text-orange-500 uppercase tracking-wider">
                        Apply to be a Tester
                      </span>
                    </button>
                  </div>
                  
                  <div className="flex items-center gap-2 mt-4">
                    <span className="w-3 h-3 bg-[#27ae60] rounded-full animate-ping absolute" />
                    <span className="w-3 h-3 bg-[#27ae60] rounded-full relative" />
                    <span className="text-sm font-bold text-[#27ae60] uppercase tracking-wide animate-pulse drop-shadow-[0_0_8px_rgba(39,174,96,0.8)]">
                      Available Now
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Laser Cutting Edition - NOW AVAILABLE */}
            <div className="group">
              <div className="bg-[#2c2c2c]/90 border-2 border-[#e67e22] rounded-sm p-5 hover:bg-[#3a3a3a]/90 transition-all hover:shadow-[0_0_20px_rgba(230,126,34,0.3)]">
                <div className="flex flex-col items-center text-center">
                  <div className="w-14 h-14 bg-[#1f1f1f] border border-[#e67e22] rounded-full flex items-center justify-center mb-3 group-hover:bg-[#e67e22]/20 transition-colors">
                    <Flame className="w-7 h-7 text-[#e67e22]" />
                  </div>
                  <h3 className="text-lg font-bold text-white uppercase tracking-wide mb-1" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    Laser Edition
                  </h3>
                  <p className="text-xs text-[#a3a3a3] mb-3">CO2 & Diode Lasers</p>
                  
                  {/* Two buttons - Login and Apply */}
                  <div className="flex flex-col gap-2 w-full">
                    <Link to="/auth?discipline=laser_cutting" className="w-full">
                      <button className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-[#e67e22] hover:bg-[#d35400] rounded-sm transition-colors">
                        <span className="text-sm font-bold text-white uppercase tracking-wider">
                          Tester Login
                        </span>
                        <ArrowRight className="w-4 h-4 text-white" />
                      </button>
                    </Link>
                    <button 
                      onClick={() => openTesterForm('laser_cutting')}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-[#2c2c2c] hover:bg-[#3a3a3a] border border-[#e67e22] rounded-sm transition-colors"
                    >
                      <span className="text-xs font-medium text-[#e67e22] uppercase tracking-wider">
                        Apply to be a Tester
                      </span>
                    </button>
                  </div>
                  
                  <div className="flex items-center gap-2 mt-4">
                    <span className="w-3 h-3 bg-[#27ae60] rounded-full animate-ping absolute" />
                    <span className="w-3 h-3 bg-[#27ae60] rounded-full relative" />
                    <span className="text-sm font-bold text-[#27ae60] uppercase tracking-wide animate-pulse drop-shadow-[0_0_8px_rgba(39,174,96,0.8)]">
                      Available Now
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Status Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 border border-[#e67e22]/50 rounded-sm bg-black/70">
            <span className="w-2 h-2 bg-[#e67e22] rounded-full animate-pulse" />
            <span className="text-sm text-[#e5e5e5]">System ready • Tools + support in one place</span>
          </div>
          
          {/* CVP Statement */}
          <div className="mb-10 max-w-2xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4 tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Practical CNC guidance for real-world machines.
            </h2>
            <p className="text-base sm:text-lg text-[#d6d6d6] leading-relaxed">
              CNC-Helpdesk helps hobby and mid-weight CNC users configure and troubleshoot their machines using safe assumptions and experience-driven logic — reducing trial-and-error, broken tools, and wasted material.
            </p>
          </div>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <Link to="/introduction">
              <button className="px-8 py-3 text-base font-bold text-white bg-[#e67e22] hover:bg-[#d35400] border border-[#e67e22] rounded-sm uppercase tracking-wide transition-all shadow-[0_0_15px_rgba(230,126,34,0.4)] hover:shadow-[0_0_25px_rgba(230,126,34,0.5)]">
                Learn More
              </button>
            </Link>
            <a href="#features">
              <button className="px-8 py-3 text-base font-bold text-white bg-[#e67e22] hover:bg-[#d35400] border border-[#e67e22] rounded-sm uppercase tracking-wide transition-all shadow-[0_0_15px_rgba(230,126,34,0.4)] hover:shadow-[0_0_25px_rgba(230,126,34,0.5)]">
                What is CNC-Helpdesk?
              </button>
            </a>
          </div>
          
          {/* Feature Cards - Centered */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 max-w-xl mx-auto">
            <div className="bg-[#2c2c2c]/90 border border-[#3a3a3a] rounded-sm p-4 hover:border-[#e67e22]/50 transition-colors w-full sm:w-auto">
              <div className="flex items-center gap-3">
                <Calculator className="w-5 h-5 text-[#e67e22]" />
                <div className="text-left">
                  <div className="text-xs text-[#a3a3a3] uppercase tracking-wide">Calculators</div>
                  <div className="text-sm font-medium text-white">Feeds & Speeds</div>
                </div>
              </div>
            </div>
            
            <div className="bg-[#2c2c2c]/90 border border-[#3a3a3a] rounded-sm p-4 hover:border-[#e67e22]/50 transition-colors w-full sm:w-auto">
              <div className="flex items-center gap-3">
                <Zap className="w-5 h-5 text-[#e67e22]" />
                <div className="text-left">
                  <div className="text-xs text-[#a3a3a3] uppercase tracking-wide">Troubleshooting</div>
                  <div className="text-sm font-medium text-white">Chatter → Fix</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 relative" style={bgStyle(IMAGES.PLATFORM_HERO_BG)}>
        <div className="absolute inset-0 bg-black/80" />
        <div className="relative z-10 max-w-6xl mx-auto px-4">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4 uppercase tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Professional CNC Tools
          </h2>
          <p className="text-center text-[#a3a3a3] mb-12 max-w-xl mx-auto">
            A focused suite of CNC utilities designed like professional CAM software.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Calculator Card */}
            <div className="bg-[#242424] border border-[#3a3a3a] rounded-sm p-6 hover:border-[#e67e22]/50 transition-colors group">
              <div className="w-12 h-12 bg-[#1f1f1f] border border-[#3a3a3a] flex items-center justify-center mb-4 group-hover:border-[#e67e22]/50 transition-colors">
                <Calculator className="w-6 h-6 text-[#e67e22]" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Smart Calculators</h3>
              <p className="text-sm text-[#a3a3a3]">
                Material-aware feed, speed, and depth calculations for perfect cuts every time
              </p>
            </div>
            
            {/* Troubleshooting Card */}
            <div className="bg-[#242424] border border-[#3a3a3a] rounded-sm p-6 hover:border-[#e67e22]/50 transition-colors group">
              <div className="w-12 h-12 bg-[#1f1f1f] border border-[#3a3a3a] flex items-center justify-center mb-4 group-hover:border-[#e67e22]/50 transition-colors">
                <Zap className="w-6 h-6 text-[#e67e22]" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Troubleshooting</h3>
              <p className="text-sm text-[#a3a3a3]">
                Step-by-step diagnosis for burning, chatter, breakage and more
              </p>
            </div>
            
            {/* Machine Setup Card */}
            <div className="bg-[#242424] border border-[#3a3a3a] rounded-sm p-6 hover:border-[#e67e22]/50 transition-colors group">
              <div className="w-12 h-12 bg-[#1f1f1f] border border-[#3a3a3a] flex items-center justify-center mb-4 group-hover:border-[#e67e22]/50 transition-colors">
                <Wrench className="w-6 h-6 text-[#e67e22]" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Machine Setup</h3>
              <p className="text-sm text-[#a3a3a3]">
                Configure your CNC machine profile for optimized calculations
              </p>
            </div>
            
            {/* Knowledge Base Card */}
            <div className="bg-[#242424] border border-[#3a3a3a] rounded-sm p-6 hover:border-[#e67e22]/50 transition-colors group">
              <div className="w-12 h-12 bg-[#1f1f1f] border border-[#3a3a3a] flex items-center justify-center mb-4 group-hover:border-[#e67e22]/50 transition-colors">
                <svg className="w-6 h-6 text-[#e67e22]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Knowledge Base</h3>
              <p className="text-sm text-[#a3a3a3]">
                Comprehensive library of CNC techniques, materials, and best practices
              </p>
            </div>
            
            {/* Community Card */}
            <div className="bg-[#242424] border border-[#3a3a3a] rounded-sm p-6 hover:border-[#e67e22]/50 transition-colors group">
              <div className="w-12 h-12 bg-[#1f1f1f] border border-[#3a3a3a] flex items-center justify-center mb-4 group-hover:border-[#e67e22]/50 transition-colors">
                <svg className="w-6 h-6 text-[#e67e22]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Community</h3>
              <p className="text-sm text-[#a3a3a3]">
                Connect with CNC makers worldwide in specialized chat rooms
              </p>
            </div>
            
            {/* Marketplace Card */}
            <div className="bg-[#242424] border border-[#3a3a3a] rounded-sm p-6 hover:border-[#e67e22]/50 transition-colors group">
              <div className="w-12 h-12 bg-[#1f1f1f] border border-[#3a3a3a] flex items-center justify-center mb-4 group-hover:border-[#e67e22]/50 transition-colors">
                <ShoppingBag className="w-6 h-6 text-[#e67e22]" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Marketplace</h3>
              <p className="text-sm text-[#a3a3a3]">
                Browse and share CNC files, templates, and resources with the community
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Status indicator only, tester buttons are per-edition above */}
      <section className="py-12 bg-[#242424] border-t border-[#3a3a3a]">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-2 px-4 py-2 border border-[#3a3a3a] rounded-sm inline-flex">
            <span className="w-2 h-2 bg-[#27ae60] rounded-full" />
            <span className="text-xs text-[#a3a3a3]">All systems ready</span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1a1a1a] border-t border-[#3a3a3a] py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col items-center gap-6">
            
            {/* Legal Links Row */}
            <div className="flex flex-wrap justify-center gap-4 md:gap-6">
              <Link to="/what-we-do" className="text-sm text-[#a3a3a3] hover:text-[#e67e22] transition-colors">
                What the SaaS Does
              </Link>
              <span className="text-[#3a3a3a] hidden md:inline">|</span>
              <Link to="/pricing" className="text-sm text-[#a3a3a3] hover:text-[#e67e22] transition-colors">
                Pricing Info
              </Link>
              <span className="text-[#3a3a3a] hidden md:inline">|</span>
              <Link to="/terms" className="text-sm text-[#a3a3a3] hover:text-[#e67e22] transition-colors">
                Terms of Service
              </Link>
              <span className="text-[#3a3a3a] hidden md:inline">|</span>
              <Link to="/privacy" className="text-sm text-[#a3a3a3] hover:text-[#e67e22] transition-colors">
                Privacy Policy
              </Link>
              <span className="text-[#3a3a3a] hidden md:inline">|</span>
              <Link to="/safety" className="text-sm text-[#a3a3a3] hover:text-[#e67e22] transition-colors">
                Safety Disclaimer
              </Link>
            </div>
            
            {/* Admin Login Button */}
            <Link to="/auth?discipline=admin">
              <button 
                data-testid="admin-login-btn"
                className="flex items-center gap-2 px-6 py-2 bg-[#2c2c2c] hover:bg-[#3a3a3a] border border-[#3a3a3a] hover:border-[#e67e22] rounded-sm transition-all group"
              >
                <Shield className="w-4 h-4 text-[#737373] group-hover:text-[#e67e22]" />
                <span className="text-sm font-medium text-[#a3a3a3] group-hover:text-white uppercase tracking-wider">
                  Admin Login
                </span>
              </button>
            </Link>
            
            <p className="text-xs text-[#737373] uppercase tracking-wider">
              © 2025 CNC-Helpdesk
            </p>
          </div>
        </div>
      </footer>

      {/* Tester Application Modal */}
      {showTesterForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="relative w-full max-w-2xl bg-[#1f1f1f] border border-[#3a3a3a] rounded-sm shadow-2xl max-h-[90vh] overflow-y-auto">
            {/* Close Button */}
            <button 
              onClick={closeForm}
              className="absolute top-3 right-3 p-1 text-[#737373] hover:text-white transition-colors z-10"
            >
              <X className="w-5 h-5" />
            </button>

            {formSubmitted ? (
              /* Success State */
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-[#27ae60]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-[#27ae60]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 uppercase tracking-wide" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  Application Received!
                </h3>
                <p className="text-sm text-[#a3a3a3] mb-6">
                  Thank you for applying to test the {testerFormDiscipline === '3d_printing' ? '3D Printing Edition' : testerFormDiscipline === 'laser_cutting' ? 'Laser Edition' : 'CNC Router Edition'}. We'll review your application and be in touch soon.
                </p>
                <Button 
                  onClick={closeForm}
                  className="bg-[#e67e22] hover:bg-[#d35400] text-white font-bold uppercase tracking-wide"
                >
                  Close
                </Button>
              </div>
            ) : (
              /* Form State */
              <>
                <div className="p-6 border-b border-[#3a3a3a]">
                  <div className="flex items-center gap-3 mb-2">
                    <button
                      onClick={closeForm}
                      className="p-2 text-[#737373] hover:text-white hover:bg-[#3a3a3a] rounded transition-colors"
                      title="Back"
                    >
                      <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div>
                      <h3 className="text-xl font-bold text-white uppercase tracking-wide" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                        Beta Tester Application
                      </h3>
                      <p className="text-sm text-[#a3a3a3]">
                        Join our beta testing program for {testerFormDiscipline === '3d_printing' ? '3D Printing Edition' : testerFormDiscipline === 'laser_cutting' ? 'Laser Edition' : 'CNC Router Edition'}
                      </p>
                    </div>
                  </div>
                  <div className={`inline-flex items-center gap-2 mt-3 px-3 py-1.5 rounded border ${
                    testerFormDiscipline === '3d_printing' 
                      ? 'border-orange-500 text-orange-500 bg-orange-500/10' 
                      : testerFormDiscipline === 'laser_cutting'
                      ? 'border-[#e67e22] text-[#e67e22] bg-[#e67e22]/10'
                      : 'border-[#e67e22] text-[#e67e22] bg-[#e67e22]/10'
                  }`}>
                    {testerFormDiscipline === '3d_printing' ? (
                      <Printer className="w-4 h-4" />
                    ) : testerFormDiscipline === 'laser_cutting' ? (
                      <Flame className="w-4 h-4" />
                    ) : (
                      <Router className="w-4 h-4" />
                    )}
                    <span className="text-sm font-semibold">
                      {testerFormDiscipline === '3d_printing' ? '3D Printing Edition' : testerFormDiscipline === 'laser_cutting' ? 'Laser Edition' : 'CNC Router Edition'}
                    </span>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                  {/* Name fields side by side */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="first_name" className="text-xs font-bold uppercase text-[#a3a3a3]">
                        First Name
                      </Label>
                      <Input
                        id="first_name"
                        data-testid="tester-first-name"
                        type="text"
                        placeholder="Enter your first name"
                        value={formData.first_name}
                        onChange={(e) => handleInputChange('first_name', e.target.value)}
                        className="bg-[#2c2c2c] border-[#3a3a3a] text-white placeholder:text-[#737373]"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="surname" className="text-xs font-bold uppercase text-[#a3a3a3]">
                        Surname
                      </Label>
                      <Input
                        id="surname"
                        data-testid="tester-surname"
                        type="text"
                        placeholder="Enter your surname"
                        value={formData.surname}
                        onChange={(e) => handleInputChange('surname', e.target.value)}
                        className="bg-[#2c2c2c] border-[#3a3a3a] text-white placeholder:text-[#737373]"
                        required
                      />
                    </div>
                  </div>

                  {/* Country and Email side by side */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="country" className="text-xs font-bold uppercase text-[#a3a3a3]">
                        Country
                      </Label>
                      <Select
                        value={formData.country}
                        onValueChange={(value) => handleInputChange('country', value)}
                      >
                        <SelectTrigger 
                          data-testid="tester-country"
                          className="bg-[#2c2c2c] border-[#3a3a3a] text-white"
                        >
                          <SelectValue placeholder="Select your country" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#2c2c2c] border-[#3a3a3a] max-h-[200px]">
                          {COUNTRIES.map((country) => (
                            <SelectItem key={country} value={country} className="text-white hover:bg-[#3a3a3a]">
                              {country}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-xs font-bold uppercase text-[#a3a3a3]">
                        Email Address
                      </Label>
                      <Input
                        id="email"
                        data-testid="tester-email"
                        type="email"
                        placeholder="your@email.com"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className="bg-[#2c2c2c] border-[#3a3a3a] text-white placeholder:text-[#737373]"
                        required
                      />
                    </div>
                  </div>

                  {/* Password fields side by side */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="password" className="text-xs font-bold uppercase text-[#a3a3a3]">
                        Password
                      </Label>
                      <div className="relative">
                        <Input
                          id="password"
                          data-testid="tester-password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Min 9 characters"
                          value={formData.password}
                          onChange={(e) => handleInputChange('password', e.target.value)}
                          className="bg-[#2c2c2c] border-[#3a3a3a] text-white placeholder:text-[#737373] pr-10"
                          required
                          minLength={9}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#737373] hover:text-white transition-colors"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <PasswordStrengthIndicator password={formData.password} />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="confirm_password" className="text-xs font-bold uppercase text-[#a3a3a3]">
                        Confirm Password
                      </Label>
                      <div className="relative">
                        <Input
                          id="confirm_password"
                          data-testid="tester-confirm-password"
                          type={showConfirmPassword ? 'text' : 'password'}
                          placeholder="Re-enter your password"
                          value={formData.confirm_password}
                          onChange={(e) => handleInputChange('confirm_password', e.target.value)}
                          className="bg-[#2c2c2c] border-[#3a3a3a] text-white placeholder:text-[#737373] pr-10"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#737373] hover:text-white transition-colors"
                        >
                          {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 flex gap-3">
                    <Button
                      type="button"
                      onClick={closeForm}
                      variant="outline"
                      className="flex-1 border-[#3a3a3a] text-[#a3a3a3] hover:text-white hover:bg-[#3a3a3a]"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back
                    </Button>
                    <Button
                      type="submit"
                      data-testid="tester-submit-btn"
                      disabled={isSubmitting}
                      className="flex-1 bg-[#e67e22] hover:bg-[#d35400] text-white font-bold uppercase tracking-wide flex items-center justify-center gap-2"
                    >
                      {isSubmitting ? (
                        'Submitting...'
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          Send Application
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* Notify Me Modal */}
      {showNotifyForm && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#1f1f1f] border border-[#3a3a3a] rounded-sm max-w-md w-full shadow-2xl">
            {/* Close button */}
            <button
              onClick={closeNotifyForm}
              className="absolute top-4 right-4 p-1 text-[#737373] hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            {notifySubmitted ? (
              /* Success State */
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-[#27ae60]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-[#27ae60]" />
                </div>
                <h3 className="text-xl font-bold text-white uppercase tracking-wide mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  You're on the list!
                </h3>
                <p className="text-sm text-[#a3a3a3] mb-6">
                  We'll notify you as soon as {getFeatureName()} Edition is ready.
                </p>
                <Button
                  onClick={closeNotifyForm}
                  className="bg-[#f39c12] hover:bg-[#e67e22] text-white font-bold uppercase tracking-wide"
                >
                  Close
                </Button>
              </div>
            ) : (
              /* Form State */
              <>
                <div className="p-6 border-b border-[#3a3a3a]">
                  <div className="flex items-center gap-3 mb-2">
                    {notifyFeature === '3d_printing' ? (
                      <Printer className="w-6 h-6 text-[#f39c12]" />
                    ) : (
                      <Flame className="w-6 h-6 text-[#f39c12]" />
                    )}
                    <h3 className="text-xl font-bold text-white uppercase tracking-wide" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                      Get Notified
                    </h3>
                  </div>
                  <p className="text-sm text-[#a3a3a3]">
                    Be the first to know when <span className="text-[#f39c12] font-medium">{getFeatureName()} Edition</span> launches
                  </p>
                </div>

                <form onSubmit={handleNotifySubmit} className="p-6 space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="notify_name" className="text-xs font-bold uppercase text-[#a3a3a3]">
                      Name (Optional)
                    </Label>
                    <Input
                      id="notify_name"
                      data-testid="notify-name-input"
                      type="text"
                      placeholder="Your name"
                      value={notifyName}
                      onChange={(e) => setNotifyName(e.target.value)}
                      className="bg-[#2c2c2c] border-[#3a3a3a] text-white placeholder:text-[#737373]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="notify_email" className="text-xs font-bold uppercase text-[#a3a3a3]">
                      Email Address *
                    </Label>
                    <Input
                      id="notify_email"
                      data-testid="notify-email-input"
                      type="email"
                      placeholder="your@email.com"
                      value={notifyEmail}
                      onChange={(e) => setNotifyEmail(e.target.value)}
                      className="bg-[#2c2c2c] border-[#3a3a3a] text-white placeholder:text-[#737373]"
                      required
                    />
                  </div>

                  <div className="pt-4">
                    <Button
                      type="submit"
                      data-testid="notify-submit-btn"
                      disabled={isNotifySubmitting}
                      className="w-full bg-[#f39c12] hover:bg-[#e67e22] text-white font-bold uppercase tracking-wide flex items-center justify-center gap-2"
                    >
                      {isNotifySubmitting ? (
                        'Subscribing...'
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          Notify Me
                        </>
                      )}
                    </Button>
                  </div>

                  <p className="text-xs text-[#737373] text-center">
                    We'll only email you when this feature launches. No spam.
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Landing;
